<script lang="ts" setup>
import { computed, type PropType, ref, watch, nextTick } from 'vue';

import BigNumber from '@exchange/helpers/bignumber';
import useMQBreakpoints from '@exchange/libs/composables/shared/src/lib/useMQBreakpoints';
import { MarketModel, MarketState } from '@exchange/libs/market/service/src';
import { CONSTANTS } from '@exchange/libs/utils/constants/src';
import { currencyService } from '@exchange/libs/utils/currency/src';
import { themingInstance } from '@exchange/libs/utils/theming/src';

import MarketFavoriteControl from './MarketFavoriteControl.vue';
import MiniatureLineChart from './MiniatureLineChart.vue';

const props = defineProps({
  filteredBy: { type: String },
  isActive: { type: Boolean },
  isFavorite: { type: Boolean, default: false },
  isFutures: { type: Boolean, default: false },
  market: { type: Object as PropType<MarketModel>, required: true },
  withLogo: { type: Boolean, required: true },
  withPriceChangeChart: { type: Boolean, default: false },
});
const emit = defineEmits<{
  (e: 'item:favorite-clicked', marketId: string): void;
  (e: 'item:market-clicked', marketId: string): void;
}>();

const { isXS } = useMQBreakpoints();
const { currencies } = currencyService;

const animatePriceChangingClass = ref('');

const marketUiState = computed(() => {
  if (!props.market.getMarketUiState) {
    return undefined;
  }

  return props.market.getMarketUiState();
});
const isNewMarket = computed(() => marketUiState.value === MarketState.NEW);
const fiatVolume = computed(() => {
  if (!props.market.allDay.globalVolume || !currencies.value) {
    return Number.NaN;
  }

  return new BigNumber(props.market.allDay.globalVolume).times(currencies.value[CONSTANTS.DEFAULT_FOR_CURRENCY_FORMATTING]?.defaultFiatValue ?? 0);
});

const colorVal = computed(() => {
  const getColorBasedOnPriceChange = () => {
    if (props.market.allDay.priceChange < 0) {
      return themingInstance.getColor('--v-theme-error');
    }

    if (props.market.allDay.priceChange > 0) {
      return themingInstance.getColor('--v-theme-success');
    }

    return themingInstance.getColor('--v-theme-text-error'); // same color as applied in .market-list-menu-content
  };

  if (!props.market.getMarketUiState) {
    return getColorBasedOnPriceChange();
  }

  switch (marketUiState.value) {
    case MarketState.NEW:
      return themingInstance.getColor('--v-theme-primary');
    case MarketState.SUSPENDED:
    case MarketState.IN_MAINTENANCE:
      return themingInstance.getColor('--v-theme-text-tertiary'); // font color is used to get the same color as the text
    default:
      return getColorBasedOnPriceChange();
  }
});

type MarketListClasses = {
  active?: boolean;
  suspended?: boolean;
  'in-maintenance'?: boolean;
  new?: boolean;
};
const classes = computed<MarketListClasses>(() => ({
  ...(!isXS.value && { active: props.isActive }),
  ...(marketUiState.value === MarketState.SUSPENDED && { suspended: true }),
  ...(marketUiState.value === MarketState.IN_MAINTENANCE && { 'in-maintenance': true }),
  ...(marketUiState.value === MarketState.NEW && { new: true }),
}));

const priceChangeClass = computed(() => {
  if (props.market.allDay.priceChangePercentage > 0) {
    return 'positive';
  }

  if (props.market.allDay.priceChangePercentage < 0) {
    return 'negative';
  }

  return '';
});

const changeMarket = () => {
  emit('item:market-clicked', props.market.id);
};

const favoriteClicked = () => {
  emit('item:favorite-clicked', props.market.id);
};

const defaultPrecision = {
  percentage: CONSTANTS.PRECISION.DEFAULT_PERCENTAGE,
  zero: CONSTANTS.PRECISION.ZERO,
};

watch(
  () => props.market?.lastPrice,
  (newPrice, oldPrice) => {
    let animateClass = '';

    if (newPrice > oldPrice) {
      animateClass = 'up';
    } else if (newPrice < oldPrice) {
      animateClass = 'down';
    }

    // clear class to force updating class
    animatePriceChangingClass.value = '';
    nextTick(() => {
      animatePriceChangingClass.value = animateClass;
    });
  },
);
</script>

<template>
  <tr
    class="market-list-item"
    :class="classes"
    :testid="`market-list-item-${market.base.code}_${market.quote.code}`"
    @click="changeMarket"
  >
    <!-- favorite -->
    <td class="market-list-item__favorite">
      <market-favorite-control
        :current-market-id="market.id"
        :is-favorite="isFavorite"
        @click.stop="favoriteClicked"
      />
    </td>
    <!-- logo & marketId -->
    <td>
      <div class="market-list-item__market">
        <div class="market-list-item__market-info">
          <x-logo
            :key="market.name"
            class="market-list-item__logo"
            :class="{ 'market-list-item__logo--xs': isXS }"
            :name="market.base.code"
            type="crypto"
          />
          <div class="market-list-item__market-details">
            <div class="market-list-item__market-row">
              <ot-market-id
                :market-id="market.name"
                :underemphasize-symbol="filteredBy"
              />
              <div class="market-list-item__type">
                {{ market.type }}
              </div>
              <ot-lozenge
                v-if="isFutures"
                :uppercase="false"
                :filled="false"
                text="10x"
                class="market-list-item__name-lozenge"
              />
            </div>
            <div
              v-if="isXS"
              class="market-list-item__market-volume-row"
            >
              <ot-volume-display
                :number="fiatVolume"
                class="market-list-item__market-volume"
              />
            </div>
          </div>
        </div>
      </div>
    </td>
    <!-- lastPrice -->
    <td
      v-if="!isXS"
      class="market-list-item__price"
      :class="animatePriceChangingClass"
    >
      <ot-number-display
        :precision="market.marketPrecision"
        :number="market.lastPrice"
      />
    </td>
    <!-- volume -->
    <td
      v-if="!isXS"
      class="market-list-item__volume"
    >
      <ot-volume-display :number="fiatVolume" />
    </td>
    <!-- miniature & priceChangePercentage - XS only -->
    <td v-if="isXS">
      <div class="market-list-item__price-change-chart-wrapper">
        <miniature-line-chart
          v-if="market.allDay.pricePointData.length && withPriceChangeChart"
          :chart-data="market.allDay.pricePointData"
          :chart-style="{ height: '20px', width: '70px' }"
          :line-color="colorVal"
        />
        <div class="market-list-item__price-change-wrapper">
          <div
            v-if="isNewMarket"
            class="market-list-item__new-tag"
          >
            <ot-lozenge
              :uppercase="true"
              :text="$t('modules.marketList.new')"
            />
          </div>
          <template v-else>
            <ot-number-display
              class="market-list-item__price"
              :class="animatePriceChangingClass"
              :precision="market.marketPrecision"
              :number="market.lastPrice"
            />
            <ot-number-display
              :class="priceChangeClass"
              :precision="defaultPrecision.percentage"
              :number="market.priceChangePercentageToDisplay"
              :add-plus-sign="true"
              :percentage="true"
            />
          </template>
        </div>
      </div>
    </td>
    <!-- priceChangePercentage - not XS -->
    <template v-else>
      <td
        v-if="!isNewMarket"
        class="market-list-item__price-change"
      >
        <ot-number-display
          :class="priceChangeClass"
          :precision="defaultPrecision.percentage"
          :number="market.priceChangePercentageToDisplay"
          :add-plus-sign="true"
          :percentage="true"
        />
      </td>
      <td
        v-else
        class="market-list-item__new-tag"
      >
        <ot-lozenge
          :uppercase="true"
          :text="$t('modules.marketList.new')"
        />
      </td>
    </template>
  </tr>
</template>

<style lang="scss" scoped>
/* stylelint-disable */

.market-list-item {
  height: 26px;
  border-color: transparent;
  cursor: pointer;
  user-select: none;

  --padding-x-xs: 4px;

  .xs & {
    --padding-x-xs: 0;

    height: 48px;
    border-color: rgb(var(--v-theme-elevation-1));
    font-size: var(--font-size-md);

    &:active {
      background-color: rgb(var(--v-theme-elevation-2));
    }
  }

  &__type {
    margin-left: 4px;
    color: rgb(var(--v-theme-text-tertiary));
    font-size: var(--font-size-xxs);
  }

  &__market-volume {
    color: rgb(var(--v-theme-text-tertiary));
    font-size: var(--font-size-xs);
  }

  &__favorite {
    width: var(--market-list-favorite-width);
    padding-left: var(--padding-x-xs);
  }

  &__market-info {
    display: flex;
    align-items: center;
    padding-left: var(--market-list-market-padding-left);

    .xs & {
      align-items: center;
    }
  }

  &__market-details {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  &__market-row {
    display: flex;
    align-items: center;
  }

  &__logo {
    width: 16px;
    margin-right: 8px;

    &--xs {
      width: 24px;
      margin-top: 0;
    }
  }

  &__name-lozenge {
    margin-left: 4px;
  }

  &__price {
    text-align: right;

    &.down {
      animation: 100ms blink;

      --blink-color: rgb(var(--v-theme-error));
    }

    &.up {
      animation: 100ms blink;

      --blink-color: rgb(var(--v-theme-success));
    }
  }

  &__volume {
    color: rgb(var(--v-theme-text-tertiary));
    text-align: right;
  }

  &__price-change-chart-wrapper {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
  }

  &__price-change-wrapper {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;

    .xs & {
      flex-basis: 80px;
      gap: 0;
    }
  }

  &__price-change {
    text-align: right;

    .xs & {
      text-align: left;
    }

    .positive {
      color: var(--mli-price-change-positive-color, rgb(var(--v-theme-success)));
    }

    .negative {
      color: var(--mli-price-change-positive-color, rgb(var(--v-theme-error)));
    }
  }

  &__new-tag {
    text-align: right;
  }

  @keyframes blink {
    0%,
    100% {
      color: var(--blink-color);
    }
  }
}

.market-list-item.active {
  background-color: rgb(var(--v-theme-elevation-2));
}

.market-list-item.suspended,
.market-list-item.in-maintenance {
  --mli-in-maintenance-color: rgb(var(--v-theme-text-tertiary));
  --market-id-separator-color: currentcolor;
  --mli-price-change-positive-color: var(--mli-in-maintenance-color);
  --mli-price-change-negative-color: var(--mli-in-maintenance-color);

  color: var(--mli-in-maintenance-color);
}
</style>
